import React from 'react';
import { Check, X, CreditCard, Crown, Rocket, Star, Zap } from 'lucide-react';

interface PricingProps {
  isAnnual: boolean;
  onToggleAnnual: () => void;
  onGetStarted: () => void;
}

export function PricingSection({ isAnnual, onToggleAnnual, onGetStarted }: PricingProps) {
  const plans = [
    {
      tier: 'free',
      name: 'Free Plan',
      description: 'Get started with basic features',
      monthlyPrice: 0,
      annualPrice: 0,
      features: [
        '40 Contract Views / Month',
        'Basic Contract Filtering',
        'Company Profile Setup',
        'Access to Public Contract Listings'
      ],
      aiTokens: 0,
      limitations: [
        'No AI Proposal Generation',
        'No AI Contract Matching',
        'No Advanced Insights'
      ],
      bestFor: [
        'Businesses exploring government contracts',
        'Users getting familiar with BidWin',
        'No AI features, just manual search & filtering'
      ],
      icon: Star,
      color: 'text-bw-muted'
    },
    {
      tier: 'basic',
      name: 'Growth Plan',
      description: 'Best for small businesses',
      monthlyPrice: 4000,
      annualPrice: 44000,
      features: [
        '100 Contract Views / Month',
        'AI-Driven Contract Matching',
        'BidWin Score Ranking',
        'Advanced Filtering & Insights',
        'Downloadable Proposal PDFs',
        'Company Profile Optimization'
      ],
      aiTokens: 15,
      tokenUsage: [
        'AI Proposal Generation (5 tokens)',
        'AI Proposal Update (2 tokens)'
      ],
      bestFor: [
        'Small businesses seriously bidding on contracts',
        'Companies wanting AI contract matching',
        'Users testing AI Proposal Generation'
      ],
      icon: Rocket,
      color: 'text-bw-primary',
      popular: true
    },
    {
      tier: 'pro',
      name: 'Pro Plan',
      description: 'For growing businesses',
      monthlyPrice: 6500,
      annualPrice: 71500,
      features: [
        'Unlimited Contract Views',
        'AI-Powered Contract Ranking',
        'AI-Optimized Past Performance',
        'In-Depth Pricing Breakdown',
        'Exclusive Webinars',
        'Priority Support'
      ],
      aiTokens: 50,
      tokenUsage: [
        'AI Proposal Generation (5 tokens)',
        'AI Proposal Update (2 tokens)',
        'Advanced AI Features Included'
      ],
      bestFor: [
        'Businesses bidding frequently on contracts',
        'Companies leveraging AI for proposals',
        'Those needing proposal updates & pricing models'
      ],
      icon: Zap,
      color: 'text-bw-primary',
      bestValue: true
    },
    {
      tier: 'enterprise',
      name: 'Enterprise Plan',
      description: 'For large organizations',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'All Pro Plan Features',
        'Dedicated AI Contract Strategist',
        'Personalized AI Training',
        'Custom AI Models',
        'Priority Proposal Review',
        'Automated Bidding Strategy',
        'Private Training Sessions',
        'Direct Opportunity Alerts'
      ],
      aiTokens: 150,
      bestFor: [
        'Large teams needing tailored AI strategy',
        'Organizations wanting dedicated support',
        'High-volume bidders needing automation'
      ],
      icon: Crown,
      color: 'text-fit-moderate-stroke'
    }
  ];

  return (
    <div className="py-24 bg-bw-app border-t border-bw-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-bw-ink">
            Simple, transparent pricing
          </h2>
          <p className="mt-4 text-lg text-bw-muted">
            Choose the plan that's right for your business
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="mt-12 flex justify-center items-center space-x-4">
          <span className={`text-sm ${!isAnnual ? 'text-bw-ink font-semibold' : 'text-bw-muted'}`}>
            Monthly
          </span>
          <button
            onClick={onToggleAnnual}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
              isAnnual ? 'bg-bw-primary' : 'bg-neutral-300'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-bw-surface shadow ring-0 transition duration-200 ease-in-out ${
                isAnnual ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
          <span className={`text-sm ${isAnnual ? 'text-bw-ink font-semibold' : 'text-bw-muted'}`}>
            Annual
            <span className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-bg text-success">
              1 Month Free
            </span>
          </span>
        </div>

        {/* Pricing Cards */}
        <div className="mt-12 grid gap-8 lg:grid-cols-4 lg:gap-x-8">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl border bg-bw-surface ${
                plan.popular
                  ? 'border-bw-primary shadow-card-hover ring-2 ring-bw-primary'
                  : plan.bestValue
                  ? 'border-fit-strong-stroke shadow-card-hover'
                  : 'border-bw-border shadow-card'
              } p-8`}
            >
              {plan.popular && (
                <span className="absolute top-0 -translate-y-1/2 bg-bw-primary text-white px-4 py-1 text-sm font-semibold tracking-wide rounded-full">
                  Most Popular
                </span>
              )}
              {plan.bestValue && (
                <span className="absolute top-0 -translate-y-1/2 bg-success text-white px-4 py-1 text-sm font-semibold tracking-wide rounded-full">
                  Best Value
                </span>
              )}

              <div className="mb-6">
                <div className="flex items-center">
                  <plan.icon className={`h-6 w-6 ${plan.color} mr-2`} />
                  <h3 className="text-xl font-bold text-bw-ink">{plan.name}</h3>
                </div>
                <p className="mt-2 text-bw-muted">{plan.description}</p>
                <div className="mt-4">
                  {isAnnual ? (
                    <div>
                      <div className="flex items-baseline">
                        <span className="text-4xl font-bold text-bw-ink">
                          {plan.tier === 'enterprise'
                            ? 'Contact for Pricing'
                            : plan.tier === 'basic'
                            ? '$440'
                            : plan.tier === 'pro'
                            ? '$715'
                            : '$0'}
                        </span>
                      </div>
                      {plan.monthlyPrice > 0 && plan.tier !== 'enterprise' && (
                        <p className="mt-1 text-sm text-bw-muted">
                          billed annually
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold text-bw-ink">
                        {plan.tier === 'enterprise'
                          ? 'Contact for Pricing'
                          : plan.monthlyPrice === 0
                          ? '$0'
                          : `$${Math.round(plan.monthlyPrice / 100)}`}
                      </span>
                      {plan.monthlyPrice > 0 && plan.tier !== 'enterprise' && (
                        <span className="text-bw-muted ml-2">/month</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {plan.aiTokens > 0 && (
                <div className="mb-6 p-4 bg-bw-primary-tint rounded-lg">
                  <h4 className="font-semibold text-bw-primary flex items-center">
                    <Zap className="h-4 w-4 mr-2" />
                    {plan.aiTokens} AI Tokens / Month
                  </h4>
                  {plan.tokenUsage && (
                    <ul className="mt-2 space-y-1">
                      {plan.tokenUsage.map((usage, index) => (
                        <li key={index} className="text-sm text-bw-primary">
                          • {usage}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="h-5 w-5 text-success flex-shrink-0 mr-2" />
                    <span className="text-bw-muted">{feature}</span>
                  </li>
                ))}
                {plan.limitations && plan.limitations.map((limitation, index) => (
                  <li key={index} className="flex items-start">
                    <X className="h-5 w-5 text-urgent flex-shrink-0 mr-2" />
                    <span className="text-bw-muted">{limitation}</span>
                  </li>
                ))}
              </ul>

              <div className="border-t border-bw-border pt-6 mt-6">
                <h4 className="text-sm font-semibold text-bw-ink mb-2">Best For:</h4>
                <ul className="space-y-2">
                  {plan.bestFor.map((item, index) => (
                    <li key={index} className="text-sm text-bw-muted">
                      • {item}
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={onGetStarted}
                className={`w-full mt-6 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition ${
                  plan.popular
                    ? 'bg-bw-primary text-white hover:brightness-110'
                    : plan.bestValue
                    ? 'bg-success text-white hover:brightness-110'
                    : 'bg-bw-sidebar text-white hover:brightness-125'
                }`}
              >
                <CreditCard className="h-5 w-5 mr-2" />
                Get Started for Free
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
